import { logger } from '@accedo/logger';
import { stylesheet } from 'astroturf';
import { FunctionalComponent } from 'preact';
import { useState } from 'preact/hooks';
import { config, focusCache } from '##/services/store/store';
import { useInterval, useReferredState } from '##/utils/hooks';
import { PageNavigation } from '##/utils/PageNavigation';
import { handleVideoClick } from '##/utils/TileClickHandlers';
import { addProgressToEpisodes } from '##/utils/ContinueWatchingUtil';
import { ComponentTypes, ComponentTypesUnion } from '##/utils/types';
import { isFalse, CONTINUE_WATCHING_ID } from '##/utils';
import tenApi from '##/services/tenApi';
import TealiumBridge from '##/services/analytics/Tealium/TealiumBridge';
import {
  BaseCarousel,
  CarouselData,
  TileProps,
} from '../BaseCarousel/BaseCarousel';
import ExtrasCarouselTile from '../StandardCarousel/ExtrasCarouselTile';
import PosterCarouselTile from '../StandardCarousel/PosterCarouselTile';
import FavouritesCarouselTile from '../FavouritesCarousel/FavouritesCarouselTile';
import StandardCarouselTile from '../StandardCarousel/StandardCarouselTile';
import OnNowCarouselTile from '../LiveCarousel/OnNowCarouselTile';
import { LiveCarouselItem } from '../LiveCarousel/types';

const styles = stylesheet`
  @import '##/styles/import.scss';

  .stacked-carousel-container {
    width: 100vw;
    padding-left: pxToVw(125);
    margin-top: 5vh;
  }
`;

const UPDATE_INTERVAL = 60 * 1000;

const TileMap: Partial<Record<
  ComponentTypes,
  FunctionalComponent<TileProps>
>> = {
  [ComponentTypes.Standard]: StandardCarouselTile,
  [ComponentTypes.Featured]: StandardCarouselTile,
  [ComponentTypes.Live]: OnNowCarouselTile,
  [ComponentTypes.ExtrasLogos]: ExtrasCarouselTile,
  [ComponentTypes.Poster]: PosterCarouselTile,
  [ComponentTypes.MyShows]: FavouritesCarouselTile,
};

const scrollEndBoundaryMapper: Partial<Record<ComponentTypesUnion, number>> = {
  [ComponentTypes.Standard]: 3,
  [ComponentTypes.Featured]: 4,
  [ComponentTypes.Live]: 2,
  [ComponentTypes.ExtrasLogos]: 5,
  [ComponentTypes.MyShows]: 5,
  [ComponentTypes.Poster]: 5,
};

// TODO: These need to scale to the app resolution since they are hardcoded in 720p
const carouselHeightMap: Partial<Record<ComponentTypesUnion, number>> = {
  [ComponentTypes.Standard]: 250,
  [ComponentTypes.Featured]: 250,
  [ComponentTypes.Live]: 360,
  [ComponentTypes.ExtrasLogos]: 215,
  [ComponentTypes.MyShows]: 300,
  [ComponentTypes.Poster]: 300,
};

const getCurrentDate = (): number => +new Date();

const mapProgressToLiveChannels = (
  liveChannelList: LiveCarouselItem[],
  currentTime = getCurrentDate(),
): LiveCarouselItem[] =>
  liveChannelList?.map((item) => {
    const { endTime = 0, startTime = 0 } = item?.liveShow || {};
    return {
      ...item,
      progress: Math.min(
        (currentTime / 1000 - startTime) / (endTime - startTime),
        1,
      ),
    };
  });

export const HomePageCarousel: FunctionalComponent<CarouselData> = (
  props: CarouselData,
) => {
  let { items } = props;
  const { channels, groups, images } = props;
  const isLive = props.type === ComponentTypes.Live;
  const TileComponent = TileMap[props.type] ?? StandardCarouselTile;
  const isStandard = props.type === ComponentTypes.Standard;
  const isExtras = props.type === ComponentTypes.ExtrasLogos;
  const isFavourites = props.type === ComponentTypes.MyShows;
  const [currentTime, setCurrentTime] = useState(getCurrentDate());
  const [
    liveChannelGroups,
    channelGroupsRef,
    setLiveChannelGroups,
  ] = useReferredState(isLive ? groups : []);
  const [liveItems, liveItemsRef, setLiveItems] = useReferredState(
    isLive ? channels : [],
  );

  // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-empty-function
  let onTileClick: (itemData: any) => void = () => {};

  if (isLive) {
    onTileClick = handleVideoClick;
  } else if (isStandard) {
    onTileClick = handleVideoClick;
    if (props.id !== CONTINUE_WATCHING_ID) {
      items = addProgressToEpisodes(items || []);
    }
  } else if (isExtras) {
    onTileClick = (tileData) => {
      if (tileData.apiEndpoint) {
        focusCache.tvShowDetails.$set({ isExtra: true });
        TealiumBridge.onCarouselItemClick(tileData);
        PageNavigation.navigateTo(`/show/${tileData.id}`, tileData);
      }
    };
  } else if (isFavourites) {
    onTileClick = (tileData) => {
      if (tileData.apiEndpoint) {
        TealiumBridge.onCarouselItemClick(tileData);
        PageNavigation.navigateTo(`/show/${tileData.id}`, tileData);
      } else if (tileData.isAddMoreShowsButton) {
        PageNavigation.navigateTo('/shows');
      }
    };
  } else {
    onTileClick = (tileData) => {
      if (tileData.apiEndpoint) {
        TealiumBridge.onCarouselItemClick(tileData);
        PageNavigation.navigateTo(`/show/${tileData.id}`, tileData);
      }
    };
  }

  useInterval(async () => {
    if (isLive) {
      const { app } = config.$get();
      if (app?.liveTvEndpoint) {
        try {
          const liveResponse = await tenApi.collections.getLiveChannels(3);

          const isResponseIdentical = liveResponse.channels.every(
            (value, idx) =>
              value?.liveShow?.id ===
              liveItemsRef?.current?.[idx]?.liveShow?.id,
          );

          if (!isResponseIdentical) {
            setLiveItems(liveResponse?.channels);
          }

          if (
            liveResponse.groups?.length !== channelGroupsRef?.current?.length
          ) {
            setLiveChannelGroups(liveResponse?.groups);
          }
        } catch (err) {
          logger.error(err);
        }
      }
      setCurrentTime(+new Date());
    }
  }, UPDATE_INTERVAL);

  const isLiveAndNotEnabled =
    isLive && isFalse(config.$get().app.enableLiveContent);

  if (
    (!isLive &&
      (!items?.length ||
        items.every(
          (item) =>
            !item.id || (!item.imageUrl && !item.liveShow?.posterImageUrl),
        ))) ||
    isLiveAndNotEnabled
  ) {
    // Ensure data exists
    return null;
  }

  const mappedLiveChannelItems = mapProgressToLiveChannels(
    (liveItems as LiveCarouselItem[]) || [],
    currentTime,
  );

  return (
    <div
      className={styles.stackedCarouselContainer}
      style={{ height: carouselHeightMap[props.type] }}
    >
      <BaseCarousel
        id={props.id}
        index={props.index}
        items={items}
        type={props.type}
        onTileClick={onTileClick}
        scrollEndBoundary={scrollEndBoundaryMapper[props.type] ?? 0}
        lazyLoadBoundary={7}
        title={props.title}
        tileIdsAsNavIds={props.id === CONTINUE_WATCHING_ID}
        isInbound={props.isInbound}
        liveProgress={currentTime || 0}
        channels={mappedLiveChannelItems || []}
        channelGroups={liveChannelGroups}
        Tile={TileComponent}
        images={images}
      />
    </div>
  );
};
